import { useQuery } from '@tanstack/react-query';
import { api } from '../../../../singleton/axios';
import dayjs from 'dayjs';

export interface MarketCapRecord {
  symbol: string;
  date: string;
  marketCap: number;
}

export interface SharesBasis {
  source: 'sec' | 'fmp-shares-float' | 'derived';
  asOf: string | null;
  filedAt?: string;
  form?: string;
  accession?: string;
  rejected?: string;
}

// The API rebases FMP's history onto the same share count Key Statistics
// uses; `scaling.mode` says how (see the route's description).
export interface MarketCapHistory {
  records: MarketCapRecord[];
  shares: number | null;
  sharesBasis: SharesBasis | null;
  scaling: { mode: 'scaled' | 'rebuilt' | 'raw'; factor: number | null; reason?: string };
}

export type MarketCapTimeframe = '1M' | '1Y';

export const useCompanyMarketCap = (ticker: string | undefined, timeframe: MarketCapTimeframe = '1M') => {
  return useQuery<MarketCapRecord[], Error>({
    queryKey: ['companyMarketCap', ticker, timeframe],
    queryFn: async () => {
      if (!ticker) {
        throw new Error('Ticker is required');
      }

      // Calculate date range based on timeframe
      const to = dayjs().format('YYYY-MM-DD');
      let from: string;
      let limit: number;

      if (timeframe === '1M') {
        from = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
        limit = 50; // Should be enough for 1 month of daily data
      } else {
        from = dayjs().subtract(1, 'year').format('YYYY-MM-DD');
        limit = 365; // Request 365 data points for 1 year of daily data
      }

      const params: any = { 
        ticker,
        from,
        to,
        limit: limit.toString()
      };
      
      const { data } = await api.get<MarketCapHistory>('/company-market-cap', {
        params,
      });
      // The chart only plots the rebased series; the basis metadata stays in the API response.
      return data.records;
    },
    enabled: !!ticker,
    refetchOnWindowFocus: false,
    staleTime: 2.5 * 60 * 1000, // 2.5 minutes for fresh data
  });
};